import { useState } from "react";
import { motion } from "framer-motion";
import { User, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useInstitutionPositionTypes, useInstitutionAuthorities, useInstitutionAuthorityHistory } from "../hooks";
import { InlineLoader } from "@/components/ui/pulsing-loader";
import { InstitutionAuthorityDialog } from "./InstitutionAuthorityDialog";
import { EndAuthorityDialog } from "./EndAuthorityDialog";
import type {
  AuthorityWithPerson,
  InstitutionPositionType,
  InstitutionScope,
} from "../types";

interface Props {
  institutionId: string;
  nationalOnly?: boolean;
  hideHistory?: boolean;
}

// ── Authority card ────────────────────────────────────────────────────────────

function AuthorityCard({
  positionName,
  authority,
  canAssign,
  canEnd,
  onAssign,
  onEnd,
}: {
  positionName: string;
  authority?: AuthorityWithPerson;
  canAssign: boolean;
  canEnd: boolean;
  onAssign: () => void;
  onEnd: (a: AuthorityWithPerson) => void;
}) {
  const person = authority?.minister ?? authority?.institution_member ?? null;
  const name = person?.full_name ?? null;
  const code = authority?.minister?.adb_code ?? null;

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-foreground">{positionName}</h4>
        {(canAssign || canEnd) && (
          <div className="flex shrink-0 gap-1">
            {canEnd && authority && (
              <button
                onClick={() => onEnd(authority)}
                className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                Finalizar
              </button>
            )}
            {canAssign && (
            <button
              onClick={onAssign}
              className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              {authority ? "Cambiar" : "Asignar"}
            </button>
            )}
          </div>
        )}
      </div>

      {person ? (
        <div className="flex items-center gap-3">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <User className="size-6 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{name}</p>
            {code && <p className="truncate text-xs text-muted-foreground">{code}</p>}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 text-muted-foreground">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-muted">
            <User className="size-6" />
          </div>
          <p className="text-sm italic">Sin asignar</p>
        </div>
      )}
    </div>
  );
}

// ── Section header ────────────────────────────────────────────────────────────

function SectionHeader({
  title,
  canAdd,
  onAdd,
}: {
  title: string;
  canAdd: boolean;
  onAdd?: () => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <div className="h-px flex-1 bg-border" />
      {canAdd && onAdd && (
        <Button size="sm" variant="outline" onClick={onAdd} className="h-7 gap-1 text-xs">
          <Plus className="size-3" />
          Asignar
        </Button>
      )}
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────

type LocationGroup = {
  id: string;
  name: string | null;
  code: string | null;
  authorities: AuthorityWithPerson[];
};

type AssignDialogState = {
  positionType?: InstitutionPositionType;
  scope: InstitutionScope;
  regionId?: string;
  districtId?: string;
  currentAuthority?: AuthorityWithPerson;
} | null;

export function InstitutionAuthoritiesTab({ institutionId, nationalOnly, hideHistory }: Props) {
  const { can } = usePermissions();
  const canAssign = can("institution_authority.add");
  const canEnd = can("institution_authority.end");
  const { data: nationalPositions = [], isLoading: loadingPositions } =
    useInstitutionPositionTypes(institutionId, "national");
  const { data: authorities = [], isLoading: loadingAuthorities } =
    useInstitutionAuthorities(institutionId);

  const [assignDialog, setAssignDialog] = useState<AssignDialogState>(null);
  const [endDialog, setEndDialog] = useState<AuthorityWithPerson | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const { data: history = [], isLoading: loadingHistory } =
    useInstitutionAuthorityHistory(institutionId, historyOpen);

  if (loadingPositions || loadingAuthorities) {
    return (
      <div className="flex justify-center py-12">
        <InlineLoader message="Cargando autoridades..." />
      </div>
    );
  }

  // Nacional: map positionTypeId → authority
  const nationalAuthorities = authorities.filter((a) => a.scope === "national");
  const authorityByPosition = new Map(nationalAuthorities.map((a) => [a.position_type_id, a]));

  // Regiones: group by region_id
  const regionMap = new Map<string, LocationGroup>();
  for (const auth of authorities.filter((a) => a.scope === "region")) {
    if (!auth.region_id || !auth.region) continue;
    if (!regionMap.has(auth.region_id))
      regionMap.set(auth.region_id, { ...auth.region, authorities: [] });
    regionMap.get(auth.region_id)!.authorities.push(auth);
  }
  const regionGroups = Array.from(regionMap.values()).sort(
    (a, b) => (a.code ?? "").localeCompare(b.code ?? ""),
  );

  // Distritos: group by district_id
  const districtMap = new Map<string, LocationGroup>();
  for (const auth of authorities.filter((a) => a.scope === "district")) {
    if (!auth.district_id || !auth.district) continue;
    if (!districtMap.has(auth.district_id))
      districtMap.set(auth.district_id, { ...auth.district, authorities: [] });
    districtMap.get(auth.district_id)!.authorities.push(auth);
  }
  const districtGroups = Array.from(districtMap.values()).sort(
    (a, b) => (a.code ?? "").localeCompare(b.code ?? ""),
  );

  const cardAnim = (i: number) => ({
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3, delay: i * 0.05 },
  });

  return (
    <>
      <div className="space-y-8">
        {/* ── Nacional ── */}
        <div className="space-y-4">
          <SectionHeader title="Nacional" canAdd={false} />
          {nationalPositions.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No hay posiciones nacionales configuradas.
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {nationalPositions.map((position: InstitutionPositionType, i) => (
                <motion.div key={position.id} {...cardAnim(i)}>
                  <AuthorityCard
                    positionName={position.name}
                    authority={authorityByPosition.get(position.id)}
                    canAssign={canAssign}
                    canEnd={canEnd}
                    onAssign={() =>
                      setAssignDialog({
                        positionType: position,
                        scope: "national",
                        currentAuthority: authorityByPosition.get(position.id),
                      })
                    }
                    onEnd={(a) => setEndDialog(a)}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* ── Distritos ── */}
        {!nationalOnly && (
          <div className="space-y-6">
            <SectionHeader
              title="Distritos"
              canAdd={canAssign}
              onAdd={() => setAssignDialog({ scope: "district" })}
            />
            {districtGroups.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No hay autoridades distritales registradas.
              </p>
            ) : (
              districtGroups.map((district) => (
                <div key={district.id} className="space-y-3">
                  <p className="text-sm font-medium text-foreground">
                    {district.name ?? district.code ?? district.id}
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {district.authorities.map((auth, i) => (
                      <motion.div key={auth.id} {...cardAnim(i)}>
                        <AuthorityCard
                          positionName={auth.position_type.name}
                          authority={auth}
                          canAssign={canAssign}
                          canEnd={canEnd}
                          onAssign={() =>
                            setAssignDialog({
                              positionType: auth.position_type as InstitutionPositionType,
                              scope: "district",
                              districtId: auth.district_id ?? undefined,
                              currentAuthority: auth,
                            })
                          }
                          onEnd={(a) => setEndDialog(a)}
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── Regiones ── */}
        {!nationalOnly && (
          <div className="space-y-6">
            <SectionHeader
              title="Regiones"
              canAdd={canAssign}
              onAdd={() => setAssignDialog({ scope: "region" })}
            />
            {regionGroups.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No hay autoridades regionales registradas.
              </p>
            ) : (
              regionGroups.map((region) => (
                <div key={region.id} className="space-y-3">
                  <p className="text-sm font-medium text-foreground">
                    {region.name ?? region.code ?? region.id}
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {region.authorities.map((auth, i) => (
                      <motion.div key={auth.id} {...cardAnim(i)}>
                        <AuthorityCard
                          positionName={auth.position_type.name}
                          authority={auth}
                          canAssign={canAssign}
                          canEnd={canEnd}
                          onAssign={() =>
                            setAssignDialog({
                              positionType: auth.position_type as InstitutionPositionType,
                              scope: "region",
                              regionId: auth.region_id ?? undefined,
                              currentAuthority: auth,
                            })
                          }
                          onEnd={(a) => setEndDialog(a)}
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── Historial ── */}
        {!hideHistory && <div className="space-y-3">
          <button
            onClick={() => setHistoryOpen((o) => !o)}
            className="flex w-full items-center gap-3 text-left"
          >
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Historial
            </h3>
            <div className="h-px flex-1 bg-border" />
            {historyOpen
              ? <ChevronUp className="size-4 text-muted-foreground" />
              : <ChevronDown className="size-4 text-muted-foreground" />}
          </button>

          {historyOpen && (
            loadingHistory ? (
              <div className="flex justify-center py-6">
                <InlineLoader message="Cargando historial..." />
              </div>
            ) : history.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Sin registros históricos.
              </p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Persona</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Cargo</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Ámbito</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Inicio</th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Fin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {history.map((h) => {
                      const person = h.minister ?? h.institution_member;
                      const location = h.district?.name ?? h.region?.name ?? null;
                      const scopeLabel: Record<string, string> = {
                        national: "Nacional", region: "Regional", district: "Distrital",
                      };
                      return (
                        <tr key={h.id} className="hover:bg-muted/30">
                          <td className="px-4 py-3 font-medium">{person?.full_name ?? "—"}</td>
                          <td className="px-4 py-3 text-muted-foreground">{h.position_type.name}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-0.5">
                              <Badge variant="outline" className="w-fit text-xs">
                                {scopeLabel[h.scope] ?? h.scope}
                              </Badge>
                              {location && <span className="text-xs text-muted-foreground">{location}</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{h.started_at}</td>
                          <td className="px-4 py-3 text-muted-foreground">{h.ended_at ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>}
      </div>

      {/* ── Dialogs ── */}
      {assignDialog && (
        <InstitutionAuthorityDialog
          isOpen
          onClose={() => setAssignDialog(null)}
          institutionId={institutionId}
          positionType={assignDialog.positionType}
          scope={assignDialog.scope}
          regionId={assignDialog.regionId}
          districtId={assignDialog.districtId}
          currentAuthority={assignDialog.currentAuthority}
        />
      )}

      {endDialog && (
        <EndAuthorityDialog
          isOpen
          onClose={() => setEndDialog(null)}
          authority={endDialog}
          institutionId={institutionId}
        />
      )}
    </>
  );
}
